import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Hantz } from '../../Hantz';
import { useAppDispatch, useAppSelector, useIsMultiPortfolioTenant } from '../../common/hooks';
import { Spinner } from '../../components/atoms/Spinner/Spinner';
import { AdvisorConsole } from '../../components/organisms/AdvisorConsole';
import { EmptyClients } from '../../components/organisms/EmptyClients';
import {
    advisorConsoleActions,
    getAdvisorConsoleProposals,
    getDefaultStatusFilter
} from '../../features/advisorConsole';
import { useCMSContent } from '../../common/hooks/useCmsContent';
import { initializeAdviceClient } from '../../features/client';
import { initializeGoals } from '../../features/client-goals';
import { resetEws } from '../../features/ews';
import { ADVISOR_ROUTES } from '../../routes/routeConstants';
import { Banner } from '../../components/molecules/Banner/Banner';
import { updateUserInfo } from '../../features/auth/authSlice';
import {
    initializePlanState,
    saveUserAgreementsSettings,
    getTenantBasedConfig,
    getOrganizationConfig,
    fetchCMSdata,
    fetchPortfolio,
    fetchGoeConfig
} from '../../features/global/globalSlice';
import { FEATURE_NAMES, REL_BANNER_VERSION } from '../../common/constants';
import { checkFFEnabled, getClientJourneyFlowType, isTenantAdmin } from '../../utils';
import { Toast } from '../../components/molecules/Toast/Toast';
import i18n from '../../i18n-backend';
import { Typography } from '../../components/atoms';
import { styled } from '@mui/material';
import { fetchAllAdvisors } from '../../features/adviceAdmin';

const Clients = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const cmsUtil = useCMSContent();
    const { t } = useTranslation();

    const accessToken = useAppSelector((state) => state?.auth?.oktaState?.accessToken?.accessToken);
    const tenantsList = useAppSelector((state) => state?.auth?.user?.tenants);
    const tenant = useAppSelector((state) => state?.auth?.user?.tenant);
    const userRole = useAppSelector((state) => state?.auth?.user?.userRole);
    const isInitialLoad = useRef(true);

    const user = useAppSelector((state) => state?.auth?.user);
    const rel_banner_ver = useAppSelector((state) => state?.auth?.user?.cwa?.[tenant]?.rel_banner_ver);
    const bannerCmsMessage = cmsUtil
        .getContentAsElement('advisor_console.release_banner_version.part1')
        ?.props?.children?.split('_');
    // Show Banner if rel_banner_ver is null or empty string
    const versionToConsider = bannerCmsMessage[1] || REL_BANNER_VERSION;
    const showBanner = rel_banner_ver === null || rel_banner_ver === '' || rel_banner_ver !== versionToConsider;
    const isFlow2 = getClientJourneyFlowType(tenant) === 'flow2';
    const isMultiPortfolioTenant = useIsMultiPortfolioTenant();

    const handleBannerClose = () => {
        if (tenant) {
            dispatch(
                saveUserAgreementsSettings({
                    rel_banner_ver: versionToConsider
                })
            ).then((action) => {
                if (action.type.endsWith('/fulfilled')) {
                    dispatch(
                        updateUserInfo({
                            ...user,
                            cwa: {
                                ...user.cwa,
                                [tenant]: {
                                    ...user.cwa[tenant],
                                    rel_banner_ver: versionToConsider
                                }
                            }
                        })
                    );
                }
            });
        }
    };

    const { advisorConsole } = useAppSelector((state) => ({
        // tenant: state?.global?.globalConfig?.subdomain, // prev way to get tenant, since Hantz and Advice both have same subdomain, it's feching for Hantz too
        tenant: state?.auth?.user?.tenant,
        advisorConsole: state?.advisorConsole
    }));

    const isHantzTenant = useAppSelector((state) => state?.auth?.user?.isHantzTenant);
    const advisors = useAppSelector((state) => state?.advisorAdmin?.advisors);

    const apiErrors = useAppSelector((state) => state?.global?.apiErrors);
    const proposalsError = useAppSelector((state) => state?.advisorConsole?.error);
    const orgEmail = useAppSelector((state) => state?.global?.orgInfo?.orgEmail);

    const hasApiError = !!(
        apiErrors?.getTenantBasedConfig ||
        apiErrors?.getOrganizationConfig ||
        apiErrors?.fetchCMSdata ||
        apiErrors?.fetchPortfolio ||
        apiErrors?.fetchGoeConfig ||
        proposalsError
    );

    const {
        loading = false,
        items = [],
        pagination,
        filters,
        hasFilters,
        clients = [],
        planDeletedSuccess,
        fromFilters
    } = advisorConsole || {};

    const { page, itemsPerPage } = pagination;

    const proposalPaginationSearchFFEnabled =
        checkFFEnabled(FEATURE_NAMES['PROPOSALS_PAGINATION_SEARCH_FE_AEX-2382']) || false;

    const handleRetry = useCallback(async () => {
        if (apiErrors?.getTenantBasedConfig) {
            dispatch(getTenantBasedConfig({ tenantId: tenant, i18n, isLoader: false, callCMS: false }));
        }
        if (apiErrors?.getOrganizationConfig || apiErrors?.fetchGoeConfig) {
            const orgResult = apiErrors?.getOrganizationConfig ? await dispatch(getOrganizationConfig(tenant)) : null;
            const resolvedOrgEmail = orgResult?.payload?.orgInfo?.orgEmail || orgEmail;
            if (apiErrors?.fetchGoeConfig) {
                dispatch(fetchGoeConfig({ tenant, clientemail: resolvedOrgEmail }));
            }
        }
        if (apiErrors?.fetchCMSdata) {
            const currentLanguage = i18n.language || localStorage.getItem('userLanguage') || 'en';
            dispatch(fetchCMSdata({ tenantId: tenant, language: currentLanguage }));
        }
        if (apiErrors?.fetchPortfolio) {
            dispatch(fetchPortfolio(tenant));
        }
        if (proposalsError) {
            dispatch(
                getAdvisorConsoleProposals({
                    tenant,
                    page,
                    itemsPerPage,
                    proposalPaginationSearchFFEnabled
                })
            );
        }
    }, [apiErrors, proposalsError, tenant, orgEmail, page, itemsPerPage, proposalPaginationSearchFFEnabled, dispatch]);

    useLayoutEffect(() => {
        if (proposalPaginationSearchFFEnabled && !advisorConsole?.dropDownTenantRefresh) {
            // On the first load of a fresh session, seed the flow2-aware default status filter
            // before fetching, so flow2 (Banorte) tenants default to "Complete". Guarded by
            // hasFilters so a user-applied filter is never clobbered. Seeding changes `filters`,
            // which re-runs this effect; the fetch then happens on the follow-up run using the
            // seeded status, so only a single request is issued.
            if (isInitialLoad.current && tenant && !hasFilters) {
                const defaultStatus = getDefaultStatusFilter(isFlow2, isMultiPortfolioTenant);
                if (filters.status !== defaultStatus) {
                    dispatch(advisorConsoleActions.setDefaultStatusFilter(defaultStatus));
                    return;
                }
            }
            dispatch(
                getAdvisorConsoleProposals({
                    tenant,
                    page,
                    itemsPerPage,
                    proposalPaginationSearchFFEnabled,
                    onload: isInitialLoad.current
                })
            );
            dispatch(initializePlanState({}));
            isInitialLoad.current = false;
        }
        if (advisorConsole?.dropDownTenantRefresh) {
            dispatch(advisorConsoleActions.setTenantRefresh(false));
        }
    }, [!isHantzTenant, itemsPerPage, page, filters]);

    useLayoutEffect(() => {
        if (!proposalPaginationSearchFFEnabled) {
            dispatch(getAdvisorConsoleProposals({ tenant }));
            dispatch(initializePlanState({}));
        }
    }, [!isHantzTenant]);

    useLayoutEffect(() => {
        if (isTenantAdmin(user) && !advisors?.length) {
            dispatch(fetchAllAdvisors(tenant));
        }
    }, [isTenantAdmin(user), advisors?.length, tenant, dispatch]);

    // Reset advisor-console state (filters, search, pagination, cursor, hasFilters)
    // whenever the user navigates away from the advisor console to any route, so
    // the next visit always starts clean.
    useEffect(() => {
        return () => {
            dispatch(advisorConsoleActions.clearPlanDeletedSuccess());
            if (proposalPaginationSearchFFEnabled) {
                dispatch(advisorConsoleActions.resetForTenantSwitch({ isFlow2, isMultiPortfolioTenant }));
            }
        };
    }, []);

    if (!fromFilters && (loading || userRole === undefined || tenantsList === undefined)) {
        return (
            <>
                {planDeletedSuccess && (
                    <Toast
                        heading={t('PLAN_DELETED_SUCCESSFULLY')}
                        type="confirm"
                        messages={[]}
                        handleClose={() => dispatch(advisorConsoleActions.clearPlanDeletedSuccess())}
                        isTimer
                        disappearTime={5}
                        disabled={false}
                    />
                )}
                <Spinner enabled />
            </>
        );
    }

    if (!items?.length && !hasFilters && !isHantzTenant && !hasApiError) {
        return (
            <>
                {/* TODO:: Under Progress and Cleanup */}
                {showBanner && <Banner type={'announcement'} handleClose={handleBannerClose} />}
                <StyledHeader>
                    <Typography as="h1" variant="headers.h2" flex={1} color="semantic.text.brand">
                        {t('ADVISOR_CONSOLE')}
                    </Typography>
                </StyledHeader>
                <EmptyClients />
            </>
        );
    }

    const scrollOnChange = (changeAction: () => void) => {
        window.scrollTo({ top: 0, behavior: 'smooth' });

        return changeAction();
    };

    const navigateToCreateClient = () => {
        // initialize page upon landing on /clientprofile
        dispatch(initializeAdviceClient());
        dispatch(initializeGoals());
        dispatch(resetEws());
        navigate(ADVISOR_ROUTES.CLIENT_PROFILE);
    };

    if (isHantzTenant) {
        return <Hantz accessToken={accessToken} />;
    } else {
        if (!items?.length && !hasFilters && !hasApiError) {
            return <EmptyClients />;
        } else
            return (
                <>
                    {planDeletedSuccess && (
                        <Toast
                            heading={t('PLAN_DELETED_SUCCESSFULLY')}
                            type="confirm"
                            messages={[]}
                            handleClose={() => dispatch(advisorConsoleActions.clearPlanDeletedSuccess())}
                            isTimer
                            disappearTime={5}
                            disabled={false}
                        />
                    )}
                    {/* TODO:: Under Progress and Cleanup */}
                    {showBanner && (
                        <Banner type={'announcement'} handleClose={handleBannerClose} message={bannerCmsMessage[0]} />
                    )}

                    <AdvisorConsole
                        clients={clients}
                        items={items}
                        pagination={pagination}
                        onChangePage={(page) =>
                            scrollOnChange(() =>
                                dispatch(advisorConsoleActions.changePage({ page, proposalPaginationSearchFFEnabled }))
                            )
                        }
                        onChangeItemsPerPage={(itemsPerPage) =>
                            scrollOnChange(() =>
                                dispatch(
                                    advisorConsoleActions.changeItemsPerPage({
                                        itemsPerPage,
                                        proposalPaginationSearchFFEnabled
                                    })
                                )
                            )
                        }
                        filters={filters}
                        onChangeFilters={(filters) =>
                            dispatch(advisorConsoleActions.setFilters({ filters, proposalPaginationSearchFFEnabled }))
                        }
                        navigateToCreateClient={navigateToCreateClient}
                        loading={loading}
                        hasApiError={hasApiError}
                        onRetry={handleRetry}
                    />
                </>
            );
    }
};

const StyledHeader = styled('header')(() => ({
    display: 'flex',
    alignItems: 'center',
    padding: '24px 40px 40px 40px'
}));

export default Clients;
